/**
 * DiagnosticRecommendations.tsx
 * 
 * Smart diagnostic recommendations based on error codes, institution data,
 * and health status
 */

import { FC } from 'react';
import { FiAlertCircle, FiCheckCircle, FiInfo, FiZap } from 'react-icons/fi';
import { FiCheckMatch, PlaidLinkEvent } from '../../services/plaidConnectionMonitorService';

interface DiagnosticRecommendationsProps {
  institution: FiCheckMatch | null;
  events: PlaidLinkEvent[];
  errorCode?: string | null;
  errorType?: string | null;
}

interface Recommendation {
  severity: 'critical' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  action?: string;
}

const DiagnosticRecommendations: FC<DiagnosticRecommendationsProps> = ({
  institution,
  events,
  errorCode,
  errorType
}) => {
  const recommendations: Recommendation[] = [];

  // Find error event
  const errorEvent = events.find(e => e.error_code || e.error_type);
  const actualErrorCode = errorCode || errorEvent?.error_code;
  const actualErrorType = errorType || errorEvent?.error_type;

  // OAuth requirement check
  if (institution?.oauth && actualErrorCode === 'INVALID_CREDENTIALS') {
    recommendations.push({
      severity: 'critical',
      title: 'OAuth Required',
      message: 'This institution requires OAuth authentication. Username/password login is not supported.',
      action: 'Inform member they must use "Connect with [Bank Name]" button instead of entering credentials.'
    });
  }

  // Credential errors on non-OAuth institutions
  if (!institution?.oauth && actualErrorCode === 'INVALID_CREDENTIALS') {
    recommendations.push({
      severity: 'warning',
      title: 'Invalid Credentials',
      message: 'Member entered incorrect username or password.',
      action: 'Ask member to verify their login credentials and try again. Consider password reset if issue persists.'
    });
  }

  // Institution health checks
  if (institution?.health_status === 'down') {
    recommendations.push({
      severity: 'critical',
      title: 'Institution Service Down',
      message: 'Plaid reports this institution is currently experiencing service disruptions.',
      action: 'Wait for Plaid to restore service. Check status.plaid.com for updates.'
    });
  }

  if (institution?.health_status === 'degraded') {
    recommendations.push({
      severity: 'warning',
      title: 'Institution Service Degraded',
      message: 'Plaid reports this institution is experiencing degraded performance.',
      action: 'Connection may succeed but could be slow. Consider retrying later if issues persist.'
    });
  }

  // MFA errors
  if (actualErrorCode?.includes('MFA') || actualErrorCode === 'INVALID_OTP') {
    recommendations.push({
      severity: 'warning',
      title: 'MFA/OTP Issue',
      message: 'Member had trouble with multi-factor authentication or one-time password.',
      action: 'Ensure member has access to their 2FA device/app and can receive codes promptly.'
    });
  }

  // Institution not supported
  if (actualErrorCode === 'INSTITUTION_NO_LONGER_SUPPORTED') {
    recommendations.push({
      severity: 'critical',
      title: 'Institution Not Supported',
      message: 'Plaid no longer supports this financial institution.',
      action: 'Inform member this bank cannot be connected. Suggest alternative connection methods or banks.'
    });
  }

  // Product support validation
  const requiredProducts = ['transactions', 'auth', 'identity', 'balance'];
  const unsupportedProducts = institution
    ? requiredProducts.filter(p => !institution.products.includes(p))
    : [];
  
  if (unsupportedProducts.length > 0) {
    recommendations.push({
      severity: 'critical',
      title: 'Missing Required Products',
      message: `This institution doesn't support required products: ${unsupportedProducts.join(', ')}`,
      action: 'Connection will fail or have limited functionality. Consider alternative institutions.'
    });
  }

  // Item login required
  if (actualErrorCode === 'ITEM_LOGIN_REQUIRED') {
    recommendations.push({
      severity: 'warning',
      title: 'Reconnection Required',
      message: 'Member needs to re-authenticate their connection.',
      action: 'Send member a reconnection link to update their credentials.'
    });
  }

  // General OAuth guidance (no error, just institutional characteristic)
  if (institution?.oauth && !actualErrorCode) {
    recommendations.push({
      severity: 'info',
      title: 'OAuth Institution',
      message: 'This institution requires OAuth authentication. Members will use "Connect with [Bank Name]" instead of entering credentials.',
      action: 'Ensure your integration properly handles OAuth flow. Members should see bank\'s login page in popup/redirect.'
    });
  }

  // Non-OAuth institution guidance
  if (institution && !institution.oauth && !actualErrorCode) {
    recommendations.push({
      severity: 'info',
      title: 'Credential-Based Authentication',
      message: 'This institution accepts username/password authentication. Members can enter their credentials directly.',
      action: 'Ensure proper credential validation and provide clear error messages for invalid credentials.'
    });
  }

  // Authentication methods guidance
  if (institution?.auth_metadata?.supported_methods) {
    const methods = institution.auth_metadata.supported_methods;
    if (methods.instant_auth && !actualErrorCode) {
      recommendations.push({
        severity: 'success',
        title: 'Instant Auth Supported',
        message: 'This institution supports Instant Auth for immediate account verification.',
        action: 'Best user experience - instant verification without micro-deposits.'
      });
    }
    if (methods.automated_micro_deposits && !methods.instant_auth && !actualErrorCode) {
      recommendations.push({
        severity: 'info',
        title: 'Automated Micro-Deposits Available',
        message: 'This institution supports automated micro-deposit verification.',
        action: 'Verification will take 1-2 business days. Set member expectations accordingly.'
      });
    }
  }

  // Success case
  if (institution?.health_status === 'healthy' && !actualErrorCode) {
    recommendations.push({
      severity: 'success',
      title: 'Institution Operational',
      message: 'No known issues with this institution. All systems operational.',
      action: 'If member experiences issues, likely user error or network connectivity.'
    });
  }

  // Unknown health status
  if (institution?.health_status === 'unknown' && !actualErrorCode) {
    recommendations.push({
      severity: 'info',
      title: 'Health Status Unknown',
      message: 'Current health status for this institution is not available.',
      action: 'Monitor for any connection issues. Check status.plaid.com for real-time updates.'
    });
  }

  const severityConfig = {
    critical: {
      icon: <FiAlertCircle size={20} />,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-600'
    },
    warning: {
      icon: <FiInfo size={20} />,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-600'
    },
    info: {
      icon: <FiZap size={20} />,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-600'
    },
    success: {
      icon: <FiCheckCircle size={20} />,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-600'
    }
  };

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-slate-700 mb-4">
        💡 Diagnostic Recommendations
      </h3>
      
      <div className="space-y-3">
        {recommendations.map((rec, idx) => {
          const config = severityConfig[rec.severity];
          return (
            <div
              key={idx}
              className={`${config.bgColor} border ${config.borderColor} rounded-lg p-4`}
            >
              <div className="flex items-start gap-3">
                <div className={config.iconColor}>{config.icon}</div>
                <div className="flex-1">
                  <h4 className={`font-semibold text-sm ${config.textColor} mb-1`}>
                    {rec.title}
                  </h4>
                  <p className="text-sm text-slate-700 mb-2">{rec.message}</p>
                  {rec.action && (
                    <div className="bg-white/50 px-3 py-2 rounded border border-slate-200">
                      <p className="text-xs font-medium text-slate-600">
                        <strong>Action:</strong> {rec.action}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DiagnosticRecommendations;
